import config from "env.config";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

const NOT_PROD = config.NODE_ENV !== "production";
const CORRELATION_STORAGE_KEY = "learning_correlation_id";

type DatadogUser = {
  id: string;
  eukaId?: string;
  role?: string;
};

type LogContext = Record<string, unknown>;

const createCorrelationId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getOrCreateCorrelationId = (): string => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return createCorrelationId();
  }

  const current = window.sessionStorage.getItem(CORRELATION_STORAGE_KEY);
  if (current) {
    return current;
  }

  const generated = createCorrelationId();
  window.sessionStorage.setItem(CORRELATION_STORAGE_KEY, generated);
  return generated;
};

const safeDatadogCall = (fn: () => void) => {
  try {
    fn();
  } catch {
    // Do not break app execution if Datadog is unavailable.
  }
};

const stringifyArg = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const toMessageAndContext = (args: unknown[]): { message: string; context: LogContext } => {
  if (args.length === 0) {
    return { message: "", context: {} };
  }

  const [first, ...rest] = args;
  const message = stringifyArg(first);
  const context: LogContext = {};

  if (rest.length > 0) {
    context.extra = rest;
  }

  return { message, context };
};

export const initDatadogContext = () => {
  const correlationId = getOrCreateCorrelationId();

  safeDatadogCall(() => {
    datadogRum.setGlobalContextProperty("correlation_id", correlationId);
    datadogLogs.setGlobalContextProperty("correlation_id", correlationId);
    datadogRum.setGlobalContextProperty("system", "OEX");
    datadogLogs.setGlobalContextProperty("system", "OEX");
    datadogRum.setGlobalContextProperty("app", "learning");
    datadogLogs.setGlobalContextProperty("app", "learning");
  });
};

export const setDatadogUserContext = (user: DatadogUser) => {
  safeDatadogCall(() => {
    datadogRum.setUser(user);
    datadogLogs.setUser(user);
    datadogRum.setGlobalContextProperty("user_id", user.id);
    datadogLogs.setGlobalContextProperty("user_id", user.id);
  });
};

export const logger = {
  log: (...args: unknown[]) => {
    if (NOT_PROD) {
      console.log(...args);
    }
  },
  info: (message: string, context: LogContext = {}) => {
    if (NOT_PROD) {
      console.info(message, context);
      return;
    }

    safeDatadogCall(() => {
      datadogLogs.logger.info(message, context);
    });
  },
  warn: (...args: unknown[]) => {
    if (NOT_PROD) {
      console.warn(...args);
      return;
    }

    const { message, context } = toMessageAndContext(args);
    safeDatadogCall(() => {
      datadogLogs.logger.warn(message, context);
    });
  },
  error: (...args: unknown[]) => {
    if (NOT_PROD) {
      console.error(...args);
      return;
    }

    const { message, context } = toMessageAndContext(args);
    safeDatadogCall(() => {
      datadogLogs.logger.error(message, context);
    });
  },
  group: (...args: unknown[]) => {
    if (NOT_PROD) {
      console.group(...args);
    }
  },
  groupEnd: () => {
    if (NOT_PROD) {
      console.groupEnd();
    }
  },
  groupCollapsed: (...args: unknown[]) => {
    if (NOT_PROD) {
      console.groupCollapsed(...args);
    }
  }
};
